import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import clsx from 'clsx';

export interface ComboboxOption {
  value: string;
  label: string;
  /** Segunda línea, más tenue (p. ej. la descripción de una tarifa). */
  hint?: string;
}

interface Props {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  options: ComboboxOption[];
  /** Lo que se muestra cuando no hay nada elegido y el campo no tiene foco. */
  placeholder?: string;
  /** Opción «nada»: aparece siempre la primera y limpia el valor. */
  emptyLabel?: string;
  disabled?: boolean;
  error?: string;
}

/**
 * Lista con buscador: un input que filtra opciones al escribir, para catálogos
 * largos donde un `<select>` obliga a leer de arriba abajo. Sin dependencias.
 * Se filtra por etiqueta Y por pista, sin acentos ni mayúsculas.
 */
export function Combobox({ label, value, onChange, options, placeholder = 'Buscar…', emptyLabel, disabled, error }: Props) {
  const id = useId();
  const [abierto, setAbierto] = useState(false);
  const [texto, setTexto] = useState('');
  const [activo, setActivo] = useState(0);
  const raiz = useRef<HTMLDivElement>(null);
  const lista = useRef<HTMLUListElement>(null);

  const elegida = options.find(o => o.value === value);

  const filtradas = useMemo(() => {
    const q = normalizar(texto.trim());
    const base = q ? options.filter(o => normalizar(`${o.label} ${o.hint ?? ''}`).includes(q)) : options;
    return emptyLabel && !q ? [{ value: '', label: emptyLabel }, ...base] : base;
  }, [options, texto, emptyLabel]);

  useEffect(() => { setActivo(0); }, [texto, abierto]);

  useEffect(() => {
    if (!abierto) return;
    const fuera = (e: MouseEvent | TouchEvent) => { if (raiz.current && !raiz.current.contains(e.target as Node)) cerrar(); };
    document.addEventListener('mousedown', fuera);
    document.addEventListener('touchstart', fuera);
    return () => { document.removeEventListener('mousedown', fuera); document.removeEventListener('touchstart', fuera); };
  }, [abierto]);

  useEffect(() => {
    lista.current?.querySelector<HTMLElement>(`[data-indice="${activo}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [activo]);

  const cerrar = () => { setAbierto(false); setTexto(''); };
  const elegir = (v: string) => { onChange(v); cerrar(); };

  const teclado = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!abierto && (e.key === 'ArrowDown' || e.key === 'Enter')) { setAbierto(true); e.preventDefault(); return; }
    if (!abierto) return;
    if (e.key === 'ArrowDown') { setActivo(i => Math.min(i + 1, filtradas.length - 1)); e.preventDefault(); }
    else if (e.key === 'ArrowUp') { setActivo(i => Math.max(i - 1, 0)); e.preventDefault(); }
    else if (e.key === 'Enter') { if (filtradas[activo]) elegir(filtradas[activo].value); e.preventDefault(); }
    else if (e.key === 'Escape') { cerrar(); e.preventDefault(); }
  };

  return (
    <div className="input-wrapper combobox" ref={raiz}>
      {label && <label htmlFor={id} className="input-label">{label}</label>}
      <div className={clsx('input-field', error && 'input-field--error', disabled && 'combobox--disabled')}>
        <span className="input-field__icon"><Search size={15} /></span>
        <input
          id={id}
          role="combobox"
          aria-expanded={abierto}
          aria-controls={`${id}-lista`}
          aria-autocomplete="list"
          autoComplete="off"
          className="input-field__input input-field__input--padded"
          disabled={disabled}
          value={abierto ? texto : (elegida?.label ?? '')}
          placeholder={elegida && !abierto ? undefined : placeholder}
          onFocus={() => { if (!disabled) setAbierto(true); }}
          onClick={() => { if (!disabled) setAbierto(true); }}
          onChange={e => { setTexto(e.target.value); if (!abierto) setAbierto(true); }}
          onKeyDown={teclado}
        />
        {value && !disabled ? (
          <button type="button" className="combobox__limpiar" aria-label="Quitar selección" onMouseDown={e => e.preventDefault()} onClick={() => elegir('')}><X size={14} /></button>
        ) : (
          <span className="combobox__flecha"><ChevronDown size={15} /></span>
        )}
      </div>
      {abierto && !disabled && (
        <ul id={`${id}-lista`} role="listbox" ref={lista} className="combobox__lista">
          {filtradas.length === 0 && <li className="combobox__vacio">Nada coincide con «{texto}».</li>}
          {filtradas.map((o, i) => (
            <li
              key={o.value || '__vacio'}
              role="option"
              aria-selected={o.value === value}
              data-indice={i}
              className={clsx('combobox__opcion', i === activo && 'combobox__opcion--activa', o.value === value && 'combobox__opcion--elegida')}
              onMouseDown={e => e.preventDefault()}
              onMouseEnter={() => setActivo(i)}
              onClick={() => elegir(o.value)}
            >
              <span className="combobox__etiqueta">{o.label}</span>
              {o.hint && <span className="combobox__pista">{o.hint}</span>}
            </li>
          ))}
        </ul>
      )}
      {elegida?.hint && !abierto && <p className="form-hint" style={{ marginTop: '0.25rem' }}>{elegida.hint}</p>}
      {error && <p className="input-error">{error}</p>}
    </div>
  );
}

function normalizar(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}
